/**
 * SHELL — dev-tools affordances (playtest only).
 *
 * Gated behind a dev flag so real players never see them. Enable with either:
 *   - the URL param `?dev`  (e.g. https://dailyroll.app/?dev) — persisted, or
 *   - a localStorage key    `dr.dev = '1'`  (set by hand in DevTools).
 *
 * When `?dev` is present we persist the flag (see `enableDevFlag`) so it survives
 * navigation between screens / runs without needing the param every time.
 */

import type { App } from '../app';
import { el } from './dom';
import { icons } from './icons';
import { clearTodaysPlay, isDevFlagEnabled, enableDevFlag } from '../storage/local';

/**
 * Whether dev tools should render. Reads `?dev` from the URL (persisting it) OR
 * the `dr.dev` localStorage flag. Call this before rendering any dev affordance.
 */
export function devToolsActive(): boolean {
  let fromUrl = false;
  try {
    fromUrl = new URLSearchParams(location.search).has('dev');
  } catch {
    /* no location (SSR/tests) — fall back to storage flag only */
  }
  if (fromUrl) enableDevFlag(); // persist so it survives navigation
  return fromUrl || isDevFlagEnabled();
}

/**
 * DEV button: "Retry daily run". Clears the local one-attempt-per-day lock for
 * today and starts a fresh SCORED daily run from the start (NOT practice).
 *
 * Reuses the normal run-start path `app.startRun()` (no `practice` flag), so the
 * replayed run scores and advances the streak exactly like a first attempt.
 *
 * NOTE: the server still enforces one-submit-per-day; a second submit for the
 * same day returns `duplicate`. This reset is purely so a playtester can re-drive
 * the scored run locally — that's the playtest need.
 */
export function devRetryDailyButton(app: App): HTMLButtonElement {
  return el(
    'button',
    {
      class: 'dr-btn dr-btn--ghost dr-btn--dev dr-btn--block',
      type: 'button',
      title: 'DEV: clear today’s local daily-lock and replay the scored run',
      on: {
        click: () => {
          const { dateKey } = app.store.get();
          // 1) clear the local daily-lock + today's cached result.
          clearTodaysPlay(dateKey);
          // 2) reset the store's dailyStatus so startRun's one-attempt guard passes.
          app.store.set({ dailyStatus: 'unplayed', result: null });
          // 3) start a fresh SCORED daily run from the start (NOT practice).
          void app.startRun();
        },
      },
    },
    el('span', { class: 'dr-devbadge', text: 'DEV' }),
    el('span', { html: icons.play, ariaLabel: '' }),
    el('span', { text: 'Retry daily run' }),
  );
}
